import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
import logo from '@/assets/logo.png';

interface LayoutProps {
  children: ReactNode;
  /**
   * Hide the full Footer on mobile (replaced with a minimal legal footer).
   * Used on conversion-critical flows like the wizard to reduce scroll fatigue.
   * The full footer remains on sm+ screens for SEO/internal links.
   */
  minimalFooterOnMobile?: boolean;
  /**
   * Replace the full site Header with a minimal funnel-header (logo + close link,
   * no nav, no account, no favorites). Used on conversion-critical flows like
   * the wizard to eliminate funnel leaks via top navigation.
   */
  minimalHeader?: boolean;
}

function MinimalHeader() {
  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="container mx-auto px-4 h-14 sm:h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 min-w-0" aria-label="Naar homepage MSP-Vergelijker">
          <img src={logo} alt="MSP-Vergelijker" className="h-7 sm:h-8 w-auto" />
          <span className="hidden sm:inline text-sm text-muted-foreground truncate">
            Vind jouw IT-partner
          </span>
        </Link>
        <Link
          to="/"
          className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
          aria-label="Sluit wizard en ga naar homepage"
        >
          <X className="h-4 w-4" aria-hidden="true" />
          <span>Sluit wizard</span>
        </Link>
      </div>
    </header>
  );
}

export function Layout({ children, minimalFooterOnMobile = false, minimalHeader = false }: LayoutProps) {
  return (
    <div className="flex min-h-screen flex-col">
      {/* Skip to Content Link for Accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Ga naar hoofdinhoud
      </a>
      {minimalHeader ? <MinimalHeader /> : <Header />}
      <main id="main-content" className="flex-1" role="main">
        {children}
      </main>
      {minimalFooterOnMobile ? (
        <>
          <div className="hidden sm:block">
            <Footer />
          </div>
          <footer className="sm:hidden bg-slate-900 text-white safe-bottom">
            <div className="container mx-auto px-4 py-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-400">
              <span>© {new Date().getFullYear()} MSP-Vergelijker</span>
              <a href="/privacy" className="hover:text-white">Privacy</a>
              <a href="/voorwaarden" className="hover:text-white">Voorwaarden</a>
              <a href="/cookies" className="hover:text-white">Cookies</a>
            </div>
          </footer>
        </>
      ) : (
        <Footer />
      )}
    </div>
  );
}
